import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  Header,
  Divider,
  Input,
  Statistic,
  Icon,
  Popup,
  Grid,
  Form,
  Button,
} from 'semantic-ui-react';

import { Alert, Planning, Api } from 'actions';
import Decimal from 'decimal.js';

import { ErrorLabel } from 'components';
import { colors } from 'utils/colors';

class SpendingTools extends Component {
  state = {
    provData: null, // the 'starting' values retrieved from the back-end
    error: null,
    isYearly: true,
  };

  componentDidMount() {
    const { dispatch, isCouplePlan, spendingToolData } = this.props;

    const titleTransfer = {
      3: 'Groceries',
      5: 'Restaurants',
      6: 'Shelter',
      12: 'Utilities & Maintenance',
      14: 'Household Furnishings',
      18: 'Clothing & Accessories',
      23: 'Car & Public Transit',
      26: 'Health Care',
      29: 'Personal Care',
      30: 'Recreation',
      35: 'Education',
      36: 'Reading Materials',
      38: 'Games of Chance',
      39: 'Miscellaneous',
      40: 'Income Taxes',
      41: 'Insurance & Pension',
      42: 'Gifts & Charity',
      359: 'Alcohol & Tobacco',
      1000: 'Travel',
    };

    return dispatch(Api.request('get', '/planning/expenditure/')).then(
      (data) => {
        const { province } = this.props;
        const provData = data.reduce((result, item) => {
          const tempProvince =
            item.province === 'Newfoundland and Labrador'
              ? 'Newfoundland & Labrador'
              : item.province;

          if (item.category_name === 'Total expenditure') {
            return result;
          }

          if (!result[item.type]) {
            result[item.type] = {};
          }

          if (!result[item.type][tempProvince]) {
            result[item.type][tempProvince] = {};
          }

          result[item.type][tempProvince][titleTransfer[item.category_id]] =
            item.value;

          return result;
        }, {});

        this.setState(
          {
            provData,
          },
          () => {
            if (
              !spendingToolData ||
              Object.keys(spendingToolData).length === 0
            ) {
              dispatch(
                Planning.updateBlueprintData('personalDetails', {
                  spendingToolData: {
                    retVal: null,
                    wagePct: null,
                    retVal2: null,
                    wagePct2: null,
                    blunter: this.calcAverage(),
                    manual: null,
                    ...(isCouplePlan && provData && provData.couple[province]),
                    ...(!isCouplePlan && provData && provData.single[province]),
                  },
                })
              );
            }
          }
        );
      },
      (err) =>
        dispatch(
          Alert.show({
            type: 'error',
            msg: err.non_field_errors
              ? err.non_field_errors[0]
              : err.msg || err.detail || 'Server Offline',
          })
        )
    );
  }

  componentDidUpdate(prevProps) {
    const prevSpendingToolData = prevProps.spendingToolData;
    const { spendingToolData } = this.props.data.personalDetails;
    if (!prevSpendingToolData || Object.keys(prevSpendingToolData).length === 0)
      return;

    if (
      JSON.stringify(
        prevSpendingToolData,
        Object.keys(spendingToolData).sort()
      ) !==
      JSON.stringify(spendingToolData, Object.keys(spendingToolData).sort())
    ) {
      this.handleSpendingAmount(
        this.calcSpendingAmount(this.props.spendingTool)
      );
    }
  }

  handleSpendingToolChange = (tool) => {
    const { dispatch } = this.props;

    if (!localStorage.getItem('cacheInputChanged')) {
      localStorage.setItem('cacheInputChanged', true);
      localStorage.setItem('cachePDChanged', true);
    }

    return dispatch(
      Planning.updateBlueprintData('personalDetails', {
        selectedSpendingTool: tool,
      })
    );
  };

  handleSpendingAmount = (spending) => {
    if (!localStorage.getItem('cacheInputChanged')) {
      localStorage.setItem('cacheInputChanged', true);
      localStorage.setItem('cachePDChanged', true);
    }
    this.props.dispatch(
      Planning.updateBlueprintData('personalDetails', {
        spending_amount: Math.round(spending),
      })
    );

    this.setState({
      errors: { ...this.state.errors, spending_amount: null },
    });
  };

  handleMoneyChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    const { dispatch, data } = this.props;
    const { isYearly } = this.state;

    const { spendingToolData, selectedSpendingTool } = data.personalDetails;

    const limitRegex = /^\d{1,9}(\.\d{0,2})?$/;

    if (!isYearly && selectedSpendingTool === 'personalized') {
      if (value.split('.')[1] && value.split('.')[1].length > 2) return;
      if (value && value[value.length - 1] === '.') {
        value *= 12;
        value += '.';
      } else if (
        value &&
        value[value.length - 1] === '0' &&
        value[value.length - 2] === '.'
      ) {
        value *= 12;
        value += '.0';
      } else {
        value = new Decimal(value * 12).toDecimalPlaces(2);
      }
    }

    if (value !== '' && !limitRegex.test(value)) return;
    if (value === '0' && selectedSpendingTool !== 'personalized') return;

    this.props.clearError(name);

    if (!localStorage.getItem('cacheInputChanged')) {
      localStorage.setItem('cacheInputChanged', true);
      localStorage.setItem('cachePDChanged', true);
    }

    if (selectedSpendingTool !== 'personalized') {
      dispatch(
        Planning.updateBlueprintData('personalDetails', {
          spendingToolData: {
            ...spendingToolData,
            [name]: !value ? null : Number(value),
          },
        })
      );
    } else {
      dispatch(
        Planning.updateBlueprintData('personalDetails', {
          spendingToolData: {
            ...spendingToolData,
            [name]: !value ? null : value,
          },
        })
      );
    }
  };

  handlePercentageChange = (e) => {
    const { dispatch, data } = this.props;
    const { spendingToolData } = data.personalDetails || {};
    const { name, value } = e.target;
    const limitRegex = /^[0-9]{1,9}$/;
    if (value !== '' && !limitRegex.test(value)) return;

    if (!localStorage.getItem('cacheInputChanged')) {
      localStorage.setItem('cacheInputChanged', true);
      localStorage.setItem('cachePDChanged', true);
    }

    dispatch(
      Planning.updateBlueprintData('personalDetails', {
        spendingToolData: {
          ...spendingToolData,
          [name]: Number(value) > 100 ? 100 : Number(value),
        },
      })
    );
  };

  scrollHandler = (to, duration) => {
    if (duration <= 0) return false;
    const el = document.scrollingElement || document.documentElement;
    const difference = to - el.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      el.scrollTop += perTick;
      if (el.scrollTop === to) {
        return false;
      }
      return this.scrollHandler(to, duration - 10);
    }, 10);
  };

  scrollToBottom = () => this.scrollHandler(window.screen.height - 230, 800);

  handleRadioButtonClick = (section) => {
    if (!localStorage.getItem('cacheInputChanged')) {
      localStorage.setItem('cacheInputChanged', true);
      localStorage.setItem('cachePDChanged', true);
    }
    this.clearInput();
    this.handleSpendingToolChange(section);
    this.handleSpendingAmount(this.calcSpendingAmount(section));
    this.scrollToBottom();
  };

  scrollHandler = (to, duration) => {
    if (duration <= 0) return false;
    const el = document.scrollingElement || document.documentElement;
    const difference = to - el.scrollTop;
    const perTick = (difference / duration) * 10;

    return setTimeout(() => {
      el.scrollTop += perTick;
      if (el.scrollTop === to) {
        return false;
      }
      return this.scrollHandler(to, duration - 10);
    }, 10);
  };

  scrollToBottom = () => this.scrollHandler(window.screen.height - 230, 600);

  calcSpendingAmount = (spendingTool) => {
    const { isCouplePlan, data } = this.props;
    const { spendingToolData } = data.personalDetails;
    const { retVal, wagePct, retVal2, wagePct2, blunter, manual } =
      spendingToolData;

    if (spendingTool === 'manual') return manual;

    const standardResult = isCouplePlan
      ? Math.round((retVal * wagePct + retVal2 * wagePct2) * 0.01)
      : Math.round(retVal * wagePct * 0.01);

    const average = this.calcAverage();

    const personalizedResult = this.calcPersonalizedResult(spendingToolData);

    const bigPictureResult = blunter || blunter === 0 ? blunter : average || 0;

    const to_return =
      spendingTool === 'industryStandard'
        ? standardResult
        : spendingTool === 'bigPicture'
        ? bigPictureResult
        : personalizedResult;

    return to_return;
  };

  handleRadioSectionClick = (toolName) => {
    this.clearInput();
    this.handleSpendingToolChange(toolName);
    this.scrollToBottom();
    this.handleSpendingAmount(this.calcSpendingAmount(toolName));
  };

  handleKeyPress = (e) => e.key === 'Enter' && e.preventDefault();

  clearInput = () => {
    this.setState({
      error: null,
    });
  };

  didUserModifyStartingValues = () => {
    const { data, isCouplePlan, province } = this.props;
    const { spendingToolData } = data.personalDetails;
    const { provData } = this.state;
    if (!provData || !spendingToolData) return false;
    return isCouplePlan
      ? JSON.stringify(
          spendingToolData,
          Object.keys(provData.couple[province]).sort()
        ) !==
          JSON.stringify(
            provData.couple[province],
            Object.keys(provData.couple[province]).sort()
          )
      : JSON.stringify(
          spendingToolData,
          Object.keys(provData.single[province]).sort()
        ) !==
          JSON.stringify(
            provData.single[province],
            Object.keys(provData.single[province]).sort()
          );
  };

  useEstimatesInSpendingTools = () => {
    const { dispatch, data, isCouplePlan, province } = this.props;
    const { provData } = this.state;
    const { spendingToolData } = data.personalDetails;

    if (!this.state.provData) return;

    dispatch(
      Planning.updateBlueprintData('personalDetails', {
        spendingToolData: {
          ...spendingToolData,
          ...(isCouplePlan && provData && provData.couple[province]),
          ...(!isCouplePlan && provData && provData.single[province]),
        },
      })
    );
  };

  calcAverage = () => {
    const { province, isCouplePlan } = this.props;
    const { provData } = this.state;
    return provData
      ? province
        ? isCouplePlan
          ? Object.values(provData.couple[province]).reduce(
              (total, value) => (total += value),
              0
            )
          : Object.values(provData.single[province]).reduce(
              (total, value) => (total += value),
              0
            )
        : null
      : null;
  };

  calcPersonalizedResult = (spendingToolDataDisplay) => {
    const { province, isCouplePlan } = this.props;
    const { provData, isYearly } = this.state;
    return provData
      ? province
        ? isCouplePlan
          ? isYearly
            ? Object.keys(provData.couple[province])
                .reduce(
                  (total, item) =>
                    (total += Number(spendingToolDataDisplay[item])),
                  0
                )
                .toFixed(2)
            : new Decimal(
                Object.keys(provData.couple[province]).reduce(
                  (total, item) =>
                    (total += Number(spendingToolDataDisplay[item])),
                  0
                )
              ).toFixed(2)
          : isYearly
          ? Object.keys(provData.single[province])
              .reduce(
                (total, item) =>
                  (total += Number(spendingToolDataDisplay[item])),
                0
              )
              .toFixed(2)
          : new Decimal(
              Object.keys(provData.single[province]).reduce(
                (total, item) =>
                  (total += Number(spendingToolDataDisplay[item])),
                0
              )
            ).toFixed(2)
        : null
      : null;
  };

  render() {
    const { province, isCouplePlan, spendingTool, data } = this.props;
    const { error, isYearly } = this.state;
    let { spendingToolData } = data.personalDetails;
    spendingToolData = spendingToolData || {};
    const { retVal, wagePct, retVal2, wagePct2, blunter, manual } =
      spendingToolData;

    const standardResult = isCouplePlan
      ? Math.round((retVal * wagePct + retVal2 * wagePct2) * 0.01)
      : Math.round(retVal * wagePct * 0.01);

    const average = this.calcAverage();

    const excluded = /(provData|error)/;

    const personalizedToolColumnValues = [
      [
        'Groceries',
        'Shelter',
        'Household Furnishings',
        'Car & Public Transit',
        'Health Care',
        'Recreation',
        'Alcohol & Tobacco',
        'Income Taxes',
        'Gifts & Charity',
        'Miscellaneous',
      ],
      [
        'Restaurants',
        'Utilities & Maintenance',
        'Clothing & Accessories',
        'Travel',
        'Personal Care',
        'Education',
        'Games of Chance',
        'Insurance & Pension',
        'Reading Materials',
      ],
    ];

    const spendingToolDataDisplay = { ...spendingToolData };
    if (!isYearly) {
      personalizedToolColumnValues[0].forEach((key) => {
        spendingToolDataDisplay[key] = new Decimal(
          spendingToolData[key] / 12
        ).toDecimalPlaces(2);
        if (spendingToolDataDisplay[key].toString().split('.').length === 1) {
          if (
            spendingToolData[key] &&
            spendingToolData[key][spendingToolData[key].length - 1] === '.'
          ) {
            spendingToolDataDisplay[key] = `${spendingToolDataDisplay[
              key
            ].toString()}.`;
          }
          if (
            spendingToolData[key] &&
            spendingToolData[key].length > 2 &&
            spendingToolData[key][spendingToolData[key].length - 1] === '0' &&
            spendingToolData[key][spendingToolData[key].length - 2] === '.'
          ) {
            spendingToolDataDisplay[key] = `${spendingToolDataDisplay[
              key
            ].toString()}.0`;
          }
          if (
            spendingToolData[key] &&
            spendingToolData[key].length > 2 &&
            spendingToolData[key][spendingToolData[key].length - 1] === '0' &&
            spendingToolData[key][spendingToolData[key].length - 3] === '.'
          ) {
            if (
              spendingToolData[key][spendingToolData[key].length - 1] === '0'
            ) {
              spendingToolDataDisplay[key] = `${spendingToolDataDisplay[
                key
              ].toString()}.00`;
            } else {
              spendingToolDataDisplay[key] = `${spendingToolDataDisplay[
                key
              ].toString()}0`;
            }
          }
        }
      });
      personalizedToolColumnValues[1].forEach((key) => {
        spendingToolDataDisplay[key] = new Decimal(
          spendingToolData[key] / 12
        ).toDecimalPlaces(2);
        if (
          spendingToolData[key] &&
          spendingToolData[key][spendingToolData[key].length - 1] === '.'
        ) {
          spendingToolDataDisplay[key] = `${spendingToolDataDisplay[
            key
          ].toString()}.`;
        }
      });
    }

    const personalizedResult = this.calcPersonalizedResult(
      spendingToolDataDisplay
    );

    return (
      <div>
        <RadioSection
          onClick={() => {
            this.handleRadioSectionClick('industryStandard');
          }}
        >
          <input
            type="radio"
            name="spendingTool"
            value="industryStandard"
            checked={spendingTool === 'industryStandard'}
          />
          {isCouplePlan ? (
            <Label>
              <b>Traditional:</b> Based on a percentage of you and your spouse’s
              pre-retirement income. This percentage is typically recommended to
              be 70% or below, depending on the circumstances of your household.
            </Label>
          ) : (
            <Label>
              <b>Traditional:</b> Based on a percentage of your pre-retirement
              income. This percentage is typically recommended to be 70% or
              below, depending on the circumstances of your household.
            </Label>
          )}
        </RadioSection>
        <RadioSection
          onClick={() => {
            this.handleRadioSectionClick('bigPicture');
          }}
        >
          <input
            type="radio"
            name="spendingTool"
            value="bigPicture"
            checked={spendingTool === 'bigPicture'}
          />
          {isCouplePlan ? (
            <Label>
              <b>Big Picture:</b> The big picture is important to us - we are
              looking for a general guide that fits our overall situation. Use
              the provincial average for total annual spending in retirement as
              a benchmark.
            </Label>
          ) : (
            <Label>
              <b>Big Picture:</b> The big picture is important to me - I am
              looking for a general guide that fits my overall situation. Use
              the provincial average for total annual spending in retirement as
              a benchmark.
            </Label>
          )}
        </RadioSection>
        <RadioSection
          onClick={() => {
            this.handleRadioSectionClick('personalized');
          }}
        >
          <input
            type="radio"
            name="spendingTool"
            value="personalized"
            checked={spendingTool === 'personalized'}
          />
          {isCouplePlan ? (
            <Label>
              <b>Personalized:</b> Fine details are important to us. We are
              willing to spend time and effort to look up relevant information.
              Provincial averages for different spending categories are provided
              as a reference point.
            </Label>
          ) : (
            <Label>
              <b>Personalized:</b> Fine details are important to me. I am
              willing to spend time and effort to look up relevant information.
              Provincial averages for different spending categories are provided
              as a reference point.
            </Label>
          )}
        </RadioSection>
        <div
          style={{
            marginTop: '2rem',
            'font-style': 'italic',
          }}
        >
          <strong>OR</strong>
        </div>
        <RadioSection
          onClick={() => {
            this.handleRadioSectionClick('manual');
          }}
        >
          <input
            type="radio"
            name="spendingTool"
            value="manual"
            checked={spendingTool === 'manual'}
          />
          {isCouplePlan ? (
            <Label>
              <b>Manual Entry:</b> We already know the amount and will enter it
              in the input below.
            </Label>
          ) : (
            <Label>
              <b>Manual Entry:</b> I already know the amount and I will enter it
              in the input below.
            </Label>
          )}
        </RadioSection>
        <Divider
          id="radio-tool-divider"
          style={{ margin: '-1rem auto 2rem' }}
        />
        {spendingTool ? (
          <div>
            <div style={{ textAlign: 'center' }}>
              {spendingTool === 'industryStandard' && (
                <div>
                  <Header
                    textAlign="center"
                    size="medium"
                    style={{ fontSize: '1.2rem' }}
                  >
                    On average, you may need approximately 50‑70% of your
                    pre-retirement income in retirement, to live the same
                    lifestyle you&apos;re living today.
                    <Header.Subheader
                      style={{ marginTop: '1rem', fontSize: '1.14rem' }}
                    >
                      The rule of thumb in the industry is 70%. However, if you
                      have significant expenses during your working years, such
                      as children and mortgages, but expect to be relieved of
                      these obligations by the time you retire, you should
                      consider a lower percentage (e.g., 50% or 60%). For
                      further discussion, see our
                      <a
                        href="https://www.wealthscope.ca/retirementtarget"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ padding: '4px' }}
                      >
                        <strong>Retirement Income Target</strong>
                      </a>
                      article.
                    </Header.Subheader>
                  </Header>
                  <div>
                    {isCouplePlan ? (
                      <div
                        style={{
                          margin: '2rem auto',
                          display: 'flex',
                          gap: '2rem',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            display: 'inline-block',
                            textAlign: 'left',
                            margin: '3rem 2rem 0 0',
                            verticalAlign: 'top',
                          }}
                        >
                          <label
                            style={{
                              display: 'block',
                              marginBottom: '2rem',
                              fontSize: '1.14rem',
                            }}
                          >
                            <b>Expected pre-retirement income &nbsp;&nbsp;</b>
                            <Popup
                              trigger={
                                <Icon
                                  name="question circle outline"
                                  style={{ color: 'black', fontSize: '15px' }}
                                />
                              }
                              position="left center"
                              content="In today’s dollars"
                            />
                          </label>
                          <label
                            style={{ display: 'block', fontSize: '1.14rem' }}
                          >
                            <b>Retirement spending as a percentage</b>
                          </label>
                        </div>
                        <div
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '1.14rem',
                              color: colors.blue,
                              textAlign: 'center',
                              marginBottom: '1rem',
                              paddingRight: '4rem',
                            }}
                          >
                            You
                          </div>
                          <div
                            style={{
                              marginRight: '2rem',
                              paddingRight: '2rem',
                              borderRight: '1px solid rgb(0, 0, 0, .2)',
                            }}
                          >
                            <InputSection
                              style={{ display: 'block', marginBottom: '1rem' }}
                              className={
                                this.props.errors.retVal ? 'invalid' : ''
                              }
                            >
                              <Input
                                name="retVal"
                                size="small"
                                label={{ basic: true, content: '$' }}
                                value={retVal || ''}
                                onChange={this.handleMoneyChange}
                                onKeyPress={this.handleKeyPress}
                              />
                            </InputSection>
                            <InputSection
                              style={{ display: 'block' }}
                              className={
                                this.props.errors.wagePct ? 'invalid' : ''
                              }
                            >
                              <Input
                                name="wagePct"
                                size="small"
                                label={{ basic: true, content: '%' }}
                                value={wagePct || ''}
                                onChange={this.handlePercentageChange}
                                onKeyPress={this.handleKeyPress}
                              />
                            </InputSection>
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                          }}
                        >
                          <div
                            style={{
                              fontSize: '1.14rem',
                              color: colors.blue,
                              textAlign: 'center',
                              marginBottom: '1rem',
                            }}
                          >
                            Spouse
                          </div>
                          <InputSection
                            style={{ display: 'block', marginBottom: '1rem' }}
                            className={
                              this.props.errors.retVal2 ? 'invalid' : ''
                            }
                          >
                            <Input
                              name="retVal2"
                              size="small"
                              label={{ basic: true, content: '$' }}
                              value={retVal2 || ''}
                              onChange={this.handleMoneyChange}
                              onKeyPress={this.handleKeyPress}
                            />
                          </InputSection>
                          <InputSection
                            style={{ display: 'block' }}
                            className={
                              this.props.errors.wagePct2 ? 'invalid' : ''
                            }
                          >
                            <Input
                              name="wagePct2"
                              size="small"
                              label={{ basic: true, content: '%' }}
                              value={wagePct2 || ''}
                              onChange={this.handlePercentageChange}
                              onKeyPress={this.handleKeyPress}
                            />
                          </InputSection>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          margin: '2rem auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2rem',
                        }}
                      >
                        <InputSection
                          className={this.props.errors.retVal ? 'invalid' : ''}
                        >
                          <label style={{ fontSize: '1.14rem' }}>
                            <b>Expected pre-retirement income &nbsp;&nbsp;</b>
                            <Popup
                              trigger={
                                <Icon
                                  name="question circle outline"
                                  style={{ color: 'black', fontSize: '15px' }}
                                />
                              }
                              position="left center"
                              content="In today’s dollars"
                            />
                          </label>
                          <Input
                            name="retVal"
                            size="small"
                            label={{ basic: true, content: '$' }}
                            width={8}
                            value={retVal || ''}
                            onChange={this.handleMoneyChange}
                            onKeyPress={this.handleKeyPress}
                          />
                        </InputSection>
                        <InputSection
                          className={this.props.errors.wagePct ? 'invalid' : ''}
                        >
                          <label>
                            <b>Retirement spending as a percentage</b>
                          </label>
                          <Input
                            name="wagePct"
                            size="small"
                            width={8}
                            label={{ basic: true, content: '%' }}
                            value={wagePct || ''}
                            onChange={this.handlePercentageChange}
                            onKeyPress={this.handleKeyPress}
                          />
                        </InputSection>
                      </div>
                    )}
                  </div>
                  <Statistic
                    size="small"
                    style={{ margin: 'auto', marginTop: '2rem' }}
                  >
                    <Statistic.Label>Total Expenditure</Statistic.Label>
                    <Statistic.Value
                      style={{ position: 'relative', marginTop: '0.5rem' }}
                    >
                      <Icon name="dollar" />
                      {standardResult && standardResult.toLocaleString()}
                      <ErrorLabel
                        msg={
                          error && !standardResult
                            ? 'Has to be greater than $0'
                            : null
                        }
                      />
                    </Statistic.Value>
                  </Statistic>
                </div>
              )}
              {spendingTool === 'bigPicture' && (
                <div>
                  <Header
                    size="medium"
                    textAlign="center"
                    style={{ fontSize: '1.2rem', maxWidth: '49rem' }}
                  >
                    <div>
                      The provincial average for{' '}
                      {isCouplePlan
                        ? 'a household of an individual aged 65+'
                        : 'an individual aged 65+ '}
                      in {province} is ${average && average.toLocaleString()}.
                      &nbsp;&nbsp;
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{ color: 'black', fontSize: '15px' }}
                          />
                        }
                        position="left center"
                        content="Based on the most recent Statistics Canada Survey of Household Spending"
                      />
                    </div>
                    <Header.Subheader style={{ marginTop: '.3rem' }}>
                      Use this value as a reference point and adjust it to your
                      lifestyle needs.
                    </Header.Subheader>
                  </Header>
                  <InputSection
                    style={{ marginTop: '2rem' }}
                    className={this.props.errors.blunter ? 'invalid' : ''}
                  >
                    <label style={{ fontSize: '1.14rem' }}>
                      <b>Retirement spending needs</b>
                    </label>
                    <Input
                      name="blunter"
                      size="small"
                      label={{ basic: true, content: '$' }}
                      value={blunter || ''}
                      onChange={this.handleMoneyChange}
                      onKeyPress={this.handleKeyPress}
                    />
                  </InputSection>
                </div>
              )}
              {spendingTool === 'personalized' && (
                <div>
                  <Header
                    size="medium"
                    textAlign="center"
                    style={{ fontSize: '1.2rem', maxWidth: '49rem' }}
                  >
                    <div>
                      {isCouplePlan
                        ? `The starting values are estimates for a household of an individual aged 65+ in ${province}.`
                        : `The starting values are estimates for an individual aged 65+ in ${province}.`}
                      &nbsp;&nbsp;
                      <Popup
                        trigger={
                          <Icon
                            name="question circle outline"
                            style={{ color: 'black', fontSize: '15px' }}
                          />
                        }
                        position="left center"
                      >
                        Based on averages from{' '}
                        <span style={{ fontWeight: 'bold' }}>
                          the most recent
                        </span>{' '}
                        Statistics Canada's Survey of Household Spending.
                      </Popup>
                    </div>
                    <Header.Subheader
                      style={{
                        marginTop: '.3rem',
                        fontSize: '1.14rem',
                      }}
                    >
                      Use these values as a reference point and adjust them to
                      your lifestyle needs.
                      {this.didUserModifyStartingValues() ? (
                        <div style={{ marginTop: '0.9rem', height: '36px' }}>
                          <Button
                            style={{
                              backgroundColor: colors.blue,
                              color: colors.white,
                            }}
                            onClick={this.useEstimatesInSpendingTools}
                          >
                            Use Latest Estimates
                          </Button>
                        </div>
                      ) : null}
                      {personalizedResult &&
                        Number.isNaN(Number(personalizedResult)) && (
                          <div
                            style={{
                              textAlign: 'center',
                              margin: '1rem 0 0 0',
                            }}
                          >
                            <span style={{ color: colors.red }}>
                              The spending categories have been reorganized.
                              Please click "Use Latest Estimates" to update the
                              values to the latest Statistics Canada survey.
                            </span>
                          </div>
                        )}
                      <div
                        style={{
                          textAlign: 'center',
                          margin: '1.5rem 0 2.5rem 0',
                        }}
                      >
                        <StyledSpan
                          active={!isYearly}
                          onClick={() => this.setState({ isYearly: false })}
                        >
                          Monthly
                        </StyledSpan>
                        <StyledSpan
                          active={isYearly}
                          onClick={() => this.setState({ isYearly: true })}
                        >
                          Annual
                        </StyledSpan>
                      </div>
                    </Header.Subheader>
                  </Header>
                  <Grid>
                    <Grid.Row
                      columns={2}
                      style={{
                        textAlign: 'left',
                        justifyContent: 'center',
                      }}
                    >
                      <Grid.Column style={{ flex: '0 0 350px' }}>
                        {personalizedToolColumnValues[0].map(
                          (key) =>
                            !excluded.test(key) && (
                              <Form.Field
                                inline
                                key={key}
                                style={{
                                  marginTop: '.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <StyledSemanticInput
                                  name={key}
                                  size="mini"
                                  label={{ basic: true, content: '$' }}
                                  value={
                                    spendingToolDataDisplay[key] === 0
                                      ? '0'
                                      : spendingToolDataDisplay[key] || ''
                                  }
                                  placeholder="0"
                                  onChange={this.handleMoneyChange}
                                  onKeyPress={this.handleKeyPress}
                                />
                                <label style={{ fontSize: '1.14rem' }}>
                                  {key}
                                </label>
                              </Form.Field>
                            )
                        )}
                      </Grid.Column>
                      <Grid.Column style={{ flex: '0 0 370px' }}>
                        {personalizedToolColumnValues[1].map(
                          (key) =>
                            !excluded.test(key) && (
                              <Form.Field
                                inline
                                key={key}
                                style={{
                                  marginTop: '.5rem',
                                }}
                              >
                                <StyledSemanticInput
                                  name={key}
                                  size="mini"
                                  label={{ basic: true, content: '$' }}
                                  value={
                                    spendingToolDataDisplay[key] === 0
                                      ? '0'
                                      : spendingToolDataDisplay[key] || ''
                                  }
                                  placeholder="0"
                                  onChange={this.handleMoneyChange}
                                  onKeyPress={this.handleKeyPress}
                                />
                                <label style={{ fontSize: '1.14rem' }}>
                                  {key}
                                </label>
                              </Form.Field>
                            )
                        )}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Statistic size="small" style={{ margin: '2rem 0 0' }}>
                    <Statistic.Label>Total Expenditure</Statistic.Label>
                    <Statistic.Value style={{ position: 'relative' }}>
                      <Icon name="dollar" />
                      {personalizedResult &&
                        Number(personalizedResult).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      <ErrorLabel
                        msg={
                          error && !personalizedResult
                            ? 'Has to be greater than $0'
                            : null
                        }
                      />
                    </Statistic.Value>
                  </Statistic>
                </div>
              )}
              {spendingTool === 'manual' && (
                <InputSection
                  style={{ display: 'block' }}
                  className={this.props.errors.manual ? 'invalid' : ''}
                >
                  <Input
                    name="manual"
                    size="small"
                    label={{ basic: true, content: '$' }}
                    value={manual || ''}
                    onChange={this.handleMoneyChange}
                    onKeyPress={this.handleKeyPress}
                  />
                </InputSection>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

SpendingTools.propTypes = {
  province: PropTypes.string,
  data: PropTypes.object.isRequired,
  isCouplePlan: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  spendingTool: PropTypes.string.isRequired,
  spendingToolData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  clearError: PropTypes.func.isRequired,
};

SpendingTools.defaultProps = {
  province: '',
  isCouplePlan: false,
};

export default connect(() => ({}))(SpendingTools);

const RadioSection = styled.div`
  position: relative;
  min-height: 60px;
  margin-top: 2rem;
  margin-left: 2rem;
`;

const Label = styled.label`
  position: absolute;
  top: -1.5px;
  margin-left: 5px;
  font-size: 1.2rem;
  width: 47rem;
`;

const InputSection = styled.div`
  display: inline-block;

  label {
    display: block;
    width: 300px;
    text-align: left;
  }
  input {
    font-size: 14px !important;
  }
  &.invalid {
    .input {
      input {
        border: 1px solid ${colors.red} !important;
        background: rgb(255, 240, 240) !important;
      }
      .label {
        border: 1px solid ${colors.red} !important;
        background: rgb(255, 240, 240) !important;
      }
    }
  }
`;

const StyledSemanticInput = styled(({ children, className, ...rest }) => (
  <Input label={{ basic: true, content: '$' }} className={className} {...rest}>
    {children}
  </Input>
))`
  .label {
    font-size: 14px !important;
    padding-top: 0.7rem !important;
  }
  input {
    width: 100px;
    padding-top: 0.7rem !important;
    font-size: 0.9rem !important;
    margin-right: 0.5rem !important;
  }
  &.invalid {
    input {
      border: 1px solid ${colors.red} !important;
      background: rgb(255, 240, 240) !important;
    }
    .label {
      border: 1px solid ${colors.red} !important;
      background: rgb(255, 240, 240) !important;
    }
  }
`;

const StyledSpan = styled.span`
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  font-size: 0.8rem;
  background-color: ${(props) => (props.active ? colors.teal : 'white')};
  border: ${(props) =>
    props.active ? '1px solid transparent' : '1px solid #dcdcdc'};
  user-select: none;

  &:first-child {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }

  &:last-child {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  &:hover {
    border: 1px solid ${colors.teal};
  }
`;
